import React, { useState, useEffect, useCallback } from 'react';
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModuleAlertsPage from '../../shared/components/ModuleAlertsPage';
import localApiAdapter from '../../core/services/localApiAdapter';
import { formatBankingDate } from './bankingHelpers';

const BanqueAlertsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.banqueAlertsPage.${key}`, opts);

  const [alerts, setAlerts] = useState([]);

  const loadAlerts = useCallback(async () => {
    try {
      const [accountsRes, reconciliationsRes, discrepanciesRes, checksRes, configRes] = await Promise.all([
        localApiAdapter.get('/banking/accounts'),
        localApiAdapter.get('/banking/reconciliation'),
        localApiAdapter.get('/banking/discrepancies'),
        localApiAdapter.get('/banking/checks'),
        localApiAdapter.get('/banking/config').catch(() => null),
      ]);

      const accounts = Array.isArray(accountsRes) ? accountsRes : [];
      const reconciliations = Array.isArray(reconciliationsRes) ? reconciliationsRes : [];
      const discrepancies = Array.isArray(discrepanciesRes) ? discrepanciesRes : [];
      const checks = Array.isArray(checksRes) ? checksRes : [];
      const config = configRes && typeof configRes === 'object' ? configRes : {};
      const notifications = config.notifications || {};

      const lowBalanceThreshold = Number(notifications.low_balance_threshold) || 10000;
      const highTransactionThreshold = Number(notifications.high_transaction_threshold) || 100000;

      const mapped = [];

      const pendingReconciliations = reconciliations.filter((r) =>
        ['pending', 'in_progress'].includes(String(r.status || '').toLowerCase()),
      );
      if (pendingReconciliations.length > 0) {
        mapped.push({
          id: 'reconciliation-pending',
          type: 'warning',
          priority: 'high',
          title: p('alert_reconciliation_title'),
          message: p('alert_reconciliation_message'),
          date: formatBankingDate(new Date()),
          read: false,
        });
      }

      const openDiscrepancies = discrepancies.filter((d) =>
        ['open', 'pending', 'in_progress'].includes(String(d.status || '').toLowerCase()),
      );
      if (openDiscrepancies.length > 0) {
        mapped.push({
          id: 'discrepancies-open',
          type: 'info',
          priority: 'medium',
          title: p('alert_statements_title'),
          message: p('alert_statements_message'),
          date: formatBankingDate(new Date()),
          read: false,
        });
      }

      accounts
        .filter((account) => Number(account.balance) < lowBalanceThreshold)
        .forEach((account) => {
          mapped.push({
            id: `low-balance-${account.id}`,
            type: 'low_balance',
            priority: 'high',
            title: p('alert_low_balance_title'),
            message: p('alert_low_balance_message'),
            date: formatBankingDate(new Date()),
            read: false,
            amount: Number(account.balance) || 0,
          });
        });

      accounts
        .filter((account) => Number(account.balance) < 0)
        .forEach((account) => {
          mapped.push({
            id: `overdraft-${account.id}`,
            type: 'error',
            priority: 'critical',
            title: p('alert_overdraft_title'),
            message: p('alert_overdraft_message'),
            date: formatBankingDate(new Date()),
            read: false,
            amount: Number(account.balance) || 0,
          });
        });

      checks
        .filter((check) => Number(check.amount) >= highTransactionThreshold)
        .slice(0, 3)
        .forEach((check) => {
          mapped.push({
            id: `high-check-${check.id}`,
            type: 'warning',
            priority: 'high',
            title: p('alert_high_transaction_title'),
            message: p('alert_high_transaction_message'),
            date: formatBankingDate(check.issue_date || check.created_at || new Date()),
            read: false,
            amount: Number(check.amount) || 0,
          });
        });

      const pendingChecks = checks.filter((check) =>
        ['issued', 'pending', 'outstanding'].includes(String(check.status || '').toLowerCase()),
      );
      if (pendingChecks.length > 0) {
        mapped.push({
          id: 'sarie-pending',
          type: 'info',
          priority: 'medium',
          title: p('alert_sarie_title'),
          message: p('alert_sarie_message'),
          date: formatBankingDate(new Date()),
          read: pendingChecks.length === 0,
        });
      }

      setAlerts(mapped);
    } catch (error) {
      console.error('Error loading banking alerts:', error);
      setAlerts([]);
    }
  }, [p]);

  useEffect(() => {
    void loadAlerts();
  }, [loadAlerts]);

  return (
    <ModuleAlertsPage
      moduleName={p('module_name')}
      moduleIcon={Building2}
      alerts={alerts}
    />
  );
};

export default BanqueAlertsPage;
